// src/components/copilot/hooks/useMockChatStream.js
import { useState, useCallback } from "react";

// Simulated delay function
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const useMockChatStream = (profileContext) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState([]);
  const [structuredData, setStructuredData] = useState(null);

  const addDebugLog = useCallback((message) => {
    const timestamp = new Date().toISOString().split("T")[1].slice(0, -1);
    console.log(`[${timestamp}] ${message}`);
    setDebugInfo((prev) => [...prev, `[${timestamp}] ${message}`]);
  }, []);

  // Mock structured output examples
  const mockStructuredOutputs = {
    entity: {
      type: "CUSTOM",
      name: "fill_form",
      value: {
        name: "fill_form",
        structured_output: {
          entityName: "Sunrise Properties LLC",
          entityAddress: "123 Main Street, Los Angeles, CA 90012",
          entityStateOfOrganization: "CA",
          entityType: "LLC",
          entityOrganizationDate: "2020-05-17T00:00:00",
          entityNotes: "Primary investment entity for commercial properties",
          entityEINNumber: "12-3456789",
        },
        json_schema: {
          title: "Entity",
          type: "object",
          properties: {
            entityName: {
              ag_grid_col_type: "text",
              anyOf: [{ type: "string" }, { type: "null" }],
              default: null,
              description: "the name of the entity",
              display_column: true,
              form_type: "text",
              title: "Entity Name",
            },
            entityAddress: {
              ag_grid_col_type: "string",
              anyOf: [{ type: "string" }, { type: "null" }],
              default: null,
              form_type: "propertyAddress",
              nested_value: "fullAddress",
              title: "Entity Address",
            },
            entityStateOfOrganization: {
              ag_grid_col_type: "string",
              anyOf: [{ type: "string" }, { type: "null" }],
              default: null,
              description: "the state of organization for the entity",
              form_type: "select",
              options: [
                "AL",
                "AK",
                "AZ",
                "AR",
                "CA",
                "CO",
                "CT",
                "DE",
                "DC",
                "FL",
                "GA",
                "HI",
                "ID",
                "IL",
                "IN",
                "IA",
                "KS",
                "KY",
                "LA",
                "ME",
                "MA",
                "MD",
                "MI",
                "MN",
                "MS",
                "MO",
                "MT",
                "NE",
                "NV",
                "NH",
                "NJ",
                "NM",
                "NY",
                "NC",
                "ND",
                "OH",
                "OK",
                "OR",
                "PA",
                "RI",
                "SC",
                "SD",
                "TN",
                "TX",
                "UT",
                "VT",
                "VA",
                "WA",
                "WV",
                "WI",
                "WY",
              ],
              title: "State of Organization",
            },
            entityType: {
              ag_grid_col_type: "text",
              anyOf: [{ type: "string" }, { type: "null" }],
              default: null,
              description: "the type of entity",
              form_type: "select",
              options: [
                "LLC",
                "S-Corp",
                "Revocable Trust",
                "Irrevocable Trust",
                "TBD",
              ],
              title: "Entity Type",
            },
            entityOrganizationDate: {
              ag_grid_col_type: "date",
              anyOf: [
                { type: "string", format: "date-time" },
                { type: "null" },
              ],
              default: null,
              description: "the date the entity was created or organized",
              form_type: "date",
              title: "Organization Date",
            },
            entityNotes: {
              ag_grid_col_type: "stringMultiline",
              anyOf: [{ type: "string" }, { type: "null" }],
              default: null,
              form_type: "stringMultiline",
              title: "Notes",
            },
            entityEINNumber: {
              ag_grid_col_type: "text",
              anyOf: [
                { type: "string" },
                { type: "integer" },
                { type: "null" },
              ],
              default: null,
              form_type: "text",
              title: "EIN Number",
            },
          },
        },
        class_name: "Entity",
        meta: {},
      },
    },
    experience: {
      type: "CUSTOM",
      name: "fill_form",
      value: {
        name: "fill_form",
        structured_output: {
          propertyAddress: "456 Oak Avenue, San Francisco, CA 94102",
          propertyType: "Multi-Family",
          purchaseDate: "2018-03-15T00:00:00",
          purchasePrice: 850000,
          currentValue: 1200000,
          monthlyRent: 6500,
          numberOfUnits: 4,
          experienceNotes:
            "Successfully managed for 5 years with 95% occupancy",
        },
        json_schema: {
          title: "Experience",
          type: "object",
          properties: {
            propertyAddress: {
              ag_grid_col_type: "string",
              anyOf: [{ type: "string" }, { type: "null" }],
              default: null,
              form_type: "propertyAddress",
              title: "Property Address",
            },
            propertyType: {
              ag_grid_col_type: "text",
              anyOf: [{ type: "string" }, { type: "null" }],
              default: null,
              form_type: "select",
              options: [
                "Single-Family",
                "Multi-Family",
                "Commercial",
                "Mixed-Use",
              ],
              title: "Property Type",
            },
            purchaseDate: {
              ag_grid_col_type: "date",
              anyOf: [
                { type: "string", format: "date-time" },
                { type: "null" },
              ],
              default: null,
              form_type: "date",
              title: "Purchase Date",
            },
            purchasePrice: {
              ag_grid_col_type: "number",
              anyOf: [{ type: "number" }, { type: "null" }],
              default: null,
              form_type: "number",
              title: "Purchase Price",
            },
            currentValue: {
              ag_grid_col_type: "number",
              anyOf: [{ type: "number" }, { type: "null" }],
              default: null,
              form_type: "number",
              title: "Current Value",
            },
            monthlyRent: {
              ag_grid_col_type: "number",
              anyOf: [{ type: "number" }, { type: "null" }],
              default: null,
              form_type: "number",
              title: "Monthly Rent",
            },
            numberOfUnits: {
              ag_grid_col_type: "number",
              anyOf: [{ type: "integer" }, { type: "null" }],
              default: null,
              form_type: "number",
              title: "Number of Units",
            },
            experienceNotes: {
              ag_grid_col_type: "stringMultiline",
              anyOf: [{ type: "string" }, { type: "null" }],
              default: null,
              form_type: "stringMultiline",
              title: "Notes",
            },
          },
        },
        class_name: "Experience",
        meta: {},
      },
    },
    bankAccount: {
      type: "CUSTOM",
      name: "fill_form",
      value: {
        name: "fill_form",
        structured_output: {
          bankName: "Wells Fargo",
          accountType: "Business Checking",
          accountNumber: "****5678",
          routingNumber: "121000248",
          accountBalance: 125000,
          accountNotes: "Primary operating account",
        },
        json_schema: {
          title: "BankAccount",
          type: "object",
          properties: {
            bankName: {
              ag_grid_col_type: "text",
              anyOf: [{ type: "string" }, { type: "null" }],
              default: null,
              form_type: "text",
              title: "Bank Name",
            },
            accountType: {
              ag_grid_col_type: "text",
              anyOf: [{ type: "string" }, { type: "null" }],
              default: null,
              form_type: "select",
              options: [
                "Checking",
                "Savings",
                "Business Checking",
                "Money Market",
              ],
              title: "Account Type",
            },
            accountNumber: {
              ag_grid_col_type: "text",
              anyOf: [{ type: "string" }, { type: "null" }],
              default: null,
              form_type: "text",
              title: "Account Number",
            },
            routingNumber: {
              ag_grid_col_type: "text",
              anyOf: [{ type: "string" }, { type: "null" }],
              default: null,
              form_type: "text",
              title: "Routing Number",
            },
            accountBalance: {
              ag_grid_col_type: "number",
              anyOf: [{ type: "number" }, { type: "null" }],
              default: null,
              form_type: "number",
              title: "Account Balance",
            },
            accountNotes: {
              ag_grid_col_type: "stringMultiline",
              anyOf: [{ type: "string" }, { type: "null" }],
              default: null,
              form_type: "stringMultiline",
              title: "Notes",
            },
          },
        },
        class_name: "BankAccount",
        meta: {},
      },
    },
  };

  // Mock responses based on keywords
  const getMockResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();

    if (lowerMessage.includes("entity") || lowerMessage.includes("business")) {
      return {
        text: "I can help you add a business entity. Let me gather the information...",
        hasStructuredOutput: true,
        structuredOutputType: "entity",
      };
    }

    if (
      lowerMessage.includes("experience") ||
      lowerMessage.includes("property")
    ) {
      return {
        text: "I'll help you add your investment experience. Here's what I found...",
        hasStructuredOutput: true,
        structuredOutputType: "experience",
      };
    }

    if (lowerMessage.includes("bank") || lowerMessage.includes("account")) {
      return {
        text: "Let me help you add a bank account. I've prepared the form...",
        hasStructuredOutput: true,
        structuredOutputType: "bankAccount",
      };
    }

    if (lowerMessage.includes("document")) {
      return {
        text: `Based on your profile, you're missing:\n\n• Tax returns (last 2 years)\n• Bank statements (last 3 months)\n• Photo ID\n\nWould you like help uploading these?`,
        hasStructuredOutput: false,
      };
    }

    if (lowerMessage.includes("missing") || lowerMessage.includes("profile")) {
      // Build missing items list
      const missingItems = [];
      if (!profileContext.hasEntities) missingItems.push("Business entities");
      if (!profileContext.hasExperience)
        missingItems.push("Investment experience");
      if (!profileContext.hasDocuments) missingItems.push("Required documents");
      if (!profileContext.hasBankAccounts) missingItems.push("Bank accounts");

      const missingText =
        missingItems.length > 0
          ? missingItems.map((item) => `• ${item}`).join("\n")
          : "Your profile looks complete!";

      return {
        text: `Here's what's missing from your profile:\n\n${missingText}\n\nWhat would you like to add first?`,
        hasStructuredOutput: false,
        showQuickActions: true, // NEW: Flag to show action buttons
        quickActions: [
          { label: "Add Business Entity", trigger: "entity" },
          { label: "Add Investment Experience", trigger: "experience" },
          { label: "Add Bank Account", trigger: "bankAccount" },
          { label: "Upload Documents", trigger: "documents" },
        ],
      };
    }

    if (lowerMessage.includes("loan") && lowerMessage.includes("attention")) {
      return {
        text: `You have ${profileContext.loansWithConditions} loan${profileContext.loansWithConditions !== 1 ? "s" : ""} with outstanding conditions. Would you like me to show you what's needed?`,
        hasStructuredOutput: false,
      };
    }

    // Default response
    return {
      text: "I can help you with:\n• Adding business entities\n• Recording investment experience\n• Uploading documents\n• Reviewing loan requirements\n\nWhat would you like to do?",
      hasStructuredOutput: false,
      showQuickActions: true,
      quickActions: [
        { label: "Add Business Entity", trigger: "entity" },
        { label: "Add Investment Experience", trigger: "experience" },
        { label: "Add Bank Account", trigger: "bankAccount" },
        { label: "View Documents", trigger: "documents" },
      ],
    };
  };

  // Simulate streaming events
  const simulateStream = async (
    responseText,
    hasStructuredOutput,
    structuredOutputType,
    showQuickActions,
    quickActions,
  ) => {
    // Simulate run_started
    addDebugLog("✓ Run started");
    await delay(100);

    // Simulate text_message_start
    addDebugLog("✓ Text message started");
    let assistantIndex = -1;
    setMessages((m) => {
      assistantIndex = m.length;
      return [...m, { role: "assistant", content: "" }];
    });
    await delay(200);

    // Simulate text streaming in chunks
    const words = responseText.split(" ");
    for (let i = 0; i < words.length; i++) {
      const chunk = (i === 0 ? "" : " ") + words[i];
      addDebugLog(`✓ Content chunk ${i + 1}: "${chunk}"`);

      setMessages((m) => {
        const copy = [...m];
        copy[assistantIndex] = {
          ...copy[assistantIndex],
          content: copy[assistantIndex].content + chunk,
        };
        return copy;
      });

      await delay(50); // Simulate typing speed
    }

    // Add quick actions to the message if needed
    if (showQuickActions && quickActions) {
      setMessages((m) => {
        const copy = [...m];
        copy[assistantIndex] = {
          ...copy[assistantIndex],
          quickActions,
        };
        return copy;
      });
    }

    // Simulate text_message_end
    addDebugLog("✓ Text message ended");
    await delay(100);

    // Simulate structured output if needed
    if (hasStructuredOutput && structuredOutputType) {
      addDebugLog("✓ Custom event received: fill_form");
      const structuredEvent = mockStructuredOutputs[structuredOutputType];

      if (structuredEvent) {
        const { name, structured_output, json_schema, class_name, meta } =
          structuredEvent.value;

        addDebugLog(`✓ Received structured output: ${name} (${class_name})`);
        console.log("Structured Output:", structured_output);
        console.log("JSON Schema:", json_schema);

        setStructuredData({
          name,
          data: structured_output,
          schema: json_schema,
          className: class_name,
          meta,
          timestamp: new Date().toISOString(),
        });

        await delay(300);

        setMessages((m) => [
          ...m,
          {
            role: "system",
            content: `✅ I've prepared the ${class_name} form data. Review and edit if needed, then click save.`,
            structuredData: structured_output,
            schema: json_schema,
            className: class_name,
          },
        ]);
      }
    }

    // Simulate run_finished
    addDebugLog("✓ Run finished");
    setIsLoading(false);
  };

  const sendMessage = useCallback(
    async (messageText) => {
      const textToSend = messageText || input;
      if (!textToSend.trim() || isLoading) return;

      addDebugLog(`Starting to send message: "${textToSend}"`);

      const userMsg = { role: "user", content: textToSend };
      setMessages((m) => [...m, userMsg]);
      setInput("");
      setIsLoading(true);
      setError(null);
      setDebugInfo([]);

      try {
        // Get mock response
        const mockResponse = getMockResponse(textToSend);

        // Simulate network delay
        await delay(500);

        // Simulate the streaming response
        await simulateStream(
          mockResponse.text,
          mockResponse.hasStructuredOutput,
          mockResponse.structuredOutputType,
          mockResponse.showQuickActions,
          mockResponse.quickActions,
        );

        addDebugLog("Request completed successfully");
      } catch (error) {
        addDebugLog(`✗ Error: ${error.message}`);
        console.error("Mock streaming error:", error);
        setError(error.message);
        setMessages((m) => m.slice(0, -1));
        setIsLoading(false);
      }
    },
    [input, isLoading, profileContext, addDebugLog],
  );

  return {
    messages,
    input,
    setInput,
    isLoading,
    error,
    setError,
    debugInfo,
    structuredData,
    sendMessage,
  };
};
